import React, { useState, useEffect, useRef } from 'react';
import { TextField, Button, Checkbox, Container, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Typography, Box, MenuItem, Select } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { getReaders, getMode, getCameraMode, getTemplateGroups, getTicket, ticketSendEmail } from '../../api/axios';
import Error from '../Error/Error';
import Success from '../Success/Success';
import { useTranslation } from 'react-i18next';
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { getParkingMapDataUrl } from '../../assets/parkingMap';
import '../../fonts/CoFoPeshka260-260Black.otf';
import './qrgenerateticket.css';
import CZLogo from '../Logo/CZLogo.jsx';

const ResendMail = () => {
    const { t } = useTranslation('global');

    // Uzimamo ID iz URL-a
    const ticketId = new URLSearchParams(window.location.search).get("id");

    const [tickets, setTickets] = useState([]);
    const [templateGroups, setTemplateGroups] = useState([]);

    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [surname, setSurname] = useState("");
    const [dayNo, setDayNo] = useState("30");
    const [registrationPlates, setRegistrationPlates] = useState("");
    const [readerGroup, setReaderGroup] = useState("");
    const [selectedTemplateGroup, setSelectedTemplateGroup] = useState("");
    const [changeTime, setChangeTime] = useState("");

    const [mode, setMode] = useState("0");
    const [cameraMode, setCameraMode] = useState("0");

    const [errMsg, setErrMsg] = useState("");
    const [succ, setSucc] = useState("");

    const ticketRefs = useRef([]);
    const [isFullSize, setIsFullSize] = useState(false);

    const [isAllPdfGenerated, setIsAllPdfGenerated] = useState(false);
    const [pdfFiles, setPdfFiles] = useState([]);

    // 🔥 Fetch readers, groups, mode, camera mode
    useEffect(() => {
        const load = async () => {
            try {
                const groups = await getTemplateGroups();
                const mode = await getMode();
                const cam = await getCameraMode();
                setTemplateGroups(groups || []);
                setMode(mode);
                setCameraMode(cam);
            } catch (err) {
                console.error(err);
            }
        };
        load();
    }, []);

    // 🔥 Odmah učitavamo ticket preko API-ja
    useEffect(() => {
        const loadTicket = async () => {
            if (!ticketId) return;

            try {
                const res = await getTicket(ticketId);
                const ticket = {
                    ...res.ticket,
                    name: res.user.name,
                    surname: res.user.surname
                };
                console.log(ticket);
                // Postavi vrednosti u state
                setTickets([ticket]);
                setEmail(ticket.email || "");
                setName(ticket.name);
                setSurname(ticket.surname);
                setSelectedTemplateGroup(ticket.template_id);
                setReaderGroup(ticket.reader_group);
                setRegistrationPlates(ticket.registration);
                setChangeTime(ticket.change_time);
                setDayNo(ticket.day_no);
            } catch (err) {
                setErrMsg("Greška pri učitavanju karte");
            }
        };

        loadTicket();
    }, [ticketId]);

    // 🔥 Generisanje jednog PDF-a
    const generatePDF = async (index) => {
        const ticketElement = ticketRefs.current[index];
        if (!ticketElement) return null;

        const canvas = await html2canvas(ticketElement, { allowTaint: true, useCORS: true });
        const imgData = canvas.toDataURL("image/png");

        const pdf = new jsPDF({
            unit: "mm",
            format: [1080, 1920]
        });

        pdf.addImage(imgData, "PNG", 0, 0, 1080, 1920, "", "FAST");

        // Druga strana: parking mapa (poledjina fizicke karte kod duplex stampe)
        const parkingMapData = await getParkingMapDataUrl();
        pdf.addPage([1080, 1920]);
        pdf.addImage(parkingMapData, "PNG", 0, 0, 1080, 1920, "", "FAST");

        const pdfBlob = pdf.output("blob");
        return pdfBlob;
    };

    // 🔥 Slanje emaila sa PDF-om
    const handleSendEmail = () => {
        setIsFullSize(true);

        setTimeout(async () => {
            const generatedFiles = await Promise.all(
                tickets.map(async (_, index) => {
                    const pdfBlob = await generatePDF(index);
                    if (!pdfBlob) return null;
                    return new File([pdfBlob], `ticket-${index + 1}.pdf`, { type: "application/pdf" });
                })
            );

            const valid = generatedFiles.filter(f => f !== null);
            setPdfFiles(valid);
            setIsAllPdfGenerated(true);
            setIsFullSize(false);
        }, 100);
    };

    // 🔥 Kada se PDF generiše → šaljemo email
    useEffect(() => {
        if (!isAllPdfGenerated || pdfFiles.length === 0) return;

        const formData = new FormData();
        formData.append("email", email);
        pdfFiles.forEach((file) => formData.append("files[]", file));

        ticketSendEmail(formData)
            .then(() => setSucc("Email poslat!"))
            .catch(() => setErrMsg("Greška pri slanju emaila"))
            .finally(() => setEmail(""));
    }, [isAllPdfGenerated, pdfFiles]);

    return (
        <Container className="container-ticket" sx={{ p: 3, mt: 4 }} maxWidth="sm">

        {tickets.length > 0 && (
            <Box mt={0}>
                {tickets.map((ticket, index) => (
                    <Box
                        key={index}
                        ref={(el) => (ticketRefs.current[index] = el)}
                        sx={{
                            width: '1080mm',
                            height: '1920mm',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: mode == "1" ? "space-between" : "center",
                            position: 'relative',
                            backgroundColor: '#fff',
                            overflow: 'hidden',
                            padding: "0",
                            zoom: isFullSize ? { xs: "1" } : { xs: "0.1" },
                        }}
                    >
                        <Box
                            className="image-parking-container"
                            sx={{
                                width: "100%",
                                display: "flex",
                                alignItems: "stretch",
                                backgroundColor:
                                    templateGroups.find(g => g.id === ticket.template_id)?.color || "#001d4f"
                            }}
                        >
                            <Box className="image" sx={{ width: "50%" }}>
                                <CZLogo
                                    primaryColor={
                                        templateGroups.find(g => g.id === ticket.template_id)?.color || "#001d4f"
                                    }
                                    width={"100"}
                                />
                            </Box>

                            <Box
                                className="parking"
                                sx={{
                                    width: "50%",
                                    height: "auto",
                                    backgroundColor:
                                        templateGroups.find(g => g.id === ticket.template_id)?.color || "#001d4f",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center"
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: "#fff",
                                        fontSize: "7000%",
                                        fontFamily: 'CoFoPeshka',
                                        fontWeight: 600
                                    }}
                                >
                                    {(ticket.reader_group).charAt(0)}
                                    <span style={{ fontSize: "0.5em" }}>
                                        {(ticket.reader_group).slice(1)}
                                    </span>
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                width: "100%",
                                paddingTop: "7%",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "space-around"
                            }}
                        >
                            <Box className="QR" sx={{ display: "flex", width: "100%", justifyContent: "center" }}>
                                <QRCodeSVG value={ticket.ticket_id} size="2200" />
                            </Box>

                            <Box
                                sx={{
                                    textTransform: "uppercase",
                                    lineHeight: 1.3,
                                    display: "flex",
                                    paddingTop: "15%",
                                    paddingBottom: "0%",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    width: "70%",
                                    fontFamily: "Arial, sans-serif",
                                    fontWeight: "bold",
                                    fontSize: "500%"
                                }}
                            >
                                <Box className='ticket-info' sx={{ width: "100%" }}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", wordSpacing: "normal" }}>
                                            <Box sx={{ textAlign: "left" }}>Ime i prezime:</Box>
                                            <Box sx={{ display: "inline-flex", flexWrap: "wrap", justifyContent: "flex-end", textAlign: "right", maxWidth: "100%" }} >
	                                        <span>{ticket.name}&nbsp;</span>
                                            <span>{ticket.surname}</span>
                                            </Box>
                                        </Box>
                                        {cameraMode == "1" && (
                                            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", wordSpacing: "normal" }}>
                                                <Box sx={{ textAlign: "left" }}>Reg broj vozila:</Box>
                                                <Box sx={{ textAlign: "right" }}>{ticket.registration}</Box>
                                            </Box>
                                        )}
                                        <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", wordSpacing: "normal" }}>
                                            <Box sx={{ textAlign: "left" }}>Validna od:</Box>
                                            <Box sx={{ textAlign: "right" }}>{(((ticket.change_time).split(" ")[0]).split('-')).reverse().join("-")}</Box>
                                        </Box>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", wordSpacing: "normal" }}>
                                            <Box sx={{ textAlign: "left" }}>Validna do:</Box>
                                            <Box sx={{ textAlign: "right" }}>{(ticket.day_no == '0') ? "Neograničeno" : (() => {
                                                const [datePart, timePart] = ticket.change_time.split(' ');
                                                const [year, month, day] = datePart.split('-');
                                                const date = new Date(year, month - 1, day);
                                                date.setDate(date.getDate() + parseInt(ticket.day_no) - 1);
                                                return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}`;
                                            })()}</Box>
                                        </Box>
                                </Box>
                            </Box>
                        </Box>

                        {cameraMode == "1" && (
                            <Box
                                className="cardType"
                                sx={{
                                    display: "flex",
                                    backgroundColor:
                                        templateGroups.find(g => g.id === ticket.template_id)?.color || "#001d4f",
                                    width: "100%",
                                    marginTop: "5%",
                                    justifyContent: "center"
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: 'CoFoPeshka',
                                        color: "white",
                                        fontSize: "1900%",
                                        fontWeight: "600"
                                    }}
                                >
                                    PARKING KARTA
                                </Typography>
                            </Box>
                        )}
                    </Box>
                ))}

                <Box sx={{ display: "flex", justifyContent: "space-between", paddingTop: "2%" }}>
                    <Button
                        sx={{ width: "100%" }}
                        variant="contained"
                        onClick={handleSendEmail}
                        disabled={!email}
                    >
                        {t("newTicket.send")}
                    </Button>
                </Box>
            </Box>
        )}

        {errMsg && <Error message={errMsg} onClose={() => setErrMsg('')} />}
        {succ && <Success message={succ} onClose={() => setSucc('')} />}
    </Container>
    );
};

export default ResendMail;
